/**
 * Dashboard del modulo magazzino.
 *
 * Aggrega KPI, top articoli, andamento movimenti, distribuzione per categoria.
 * Tutti i dati sono filtrati per UO corrente tramite il backend.
 *
 * Regola 6.8 CLAUDE.md: importa solo da @floraltech/ui.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeftRight,
  ArrowUp,
  Clock,
  Package,
  TrendingUp,
  Warehouse,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface Kpi {
  articoli_totali: number;
  articoli_con_giacenza: number;
  articoli_esauriti: number;
  articoli_sotto_soglia: number;
  giacenza_totale: number;
  valore_magazzino: number;
  lotti_totali: number;
  lotti_scadenza_30gg: number;
  categorie_root: number;
}

interface TopArticolo {
  articolo_id: string;
  sku_interno: string;
  descrizione: string | null;
  quantita: number;
}

interface AndamentoPeriodo {
  periodo: string;
  ingressi: number;
  uscite: number;
}

interface Distribuzione {
  categoria_id: string | null;
  descrizione: string;
  count_articoli: number;
}

interface DashboardData {
  kpi: Kpi;
  top_uscite_30gg: TopArticolo[];
  top_ingressi_30gg: TopArticolo[];
  andamento_6_mesi: AndamentoPeriodo[];
  distribuzione_categorie: Distribuzione[];
}

interface SottoSogliaItem {
  articolo_id: string;
  sku_interno: string;
  descrizione: string | null;
  giacenza: number;
  soglia_riordino: number;
}

interface InScadenzaItem {
  lotto_id: string;
  articolo_id: string;
  sku_interno: string;
  numero_lotto: string | null;
  quantita_disponibile: number;
  data_scadenza: string;
  giorni_rimanenti: number;
}

interface MovimentoItem {
  id: string;
  tipo: string;
  quantita: number;
  created_at: string;
  riferimento_esterno: string | null;
}

const fmtInt = (n: number): string => new Intl.NumberFormat('it-IT').format(Math.round(n));
const fmtDec = (n: number): string =>
  new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtEur = (n: number): string =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
const monthLabel = (yyyymm: string): string => {
  const [y, m] = yyyymm.split('-');
  const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
  return `${MESI[parseInt(m, 10) - 1]} '${y.slice(2)}`;
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  accent?: 'default' | 'warning' | 'danger' | 'success';
  icon: React.ReactNode;
  onClick?: () => void;
}> = ({ label, value, sub, accent = 'default', icon, onClick }) => {
  const ring =
    accent === 'warning'
      ? 'border-amber-500/40'
      : accent === 'danger'
        ? 'border-destructive/50'
        : accent === 'success'
          ? 'border-emerald-500/40'
          : 'border-border';
  return (
    <Card
      className={`${ring} ${onClick ? 'cursor-pointer transition hover:border-primary' : ''}`}
      onClick={onClick}
    >
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <div>
          <p className="text-xs uppercase text-muted-foreground">{label}</p>
          <p className="font-outfit mt-1 text-2xl font-semibold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className="rounded-md bg-muted p-2 text-muted-foreground">{icon}</div>
      </CardContent>
    </Card>
  );
};

const MagazzinoOverview: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loadingDash, setLoadingDash] = useState(true);
  const [errorDash, setErrorDash] = useState<string | null>(null);

  const [sottoSoglia, setSottoSoglia] = useState<SottoSogliaItem[]>([]);
  const [inScadenza, setInScadenza] = useState<InScadenzaItem[]>([]);
  const [movimenti, setMovimenti] = useState<MovimentoItem[]>([]);
  const [loadingTables, setLoadingTables] = useState(true);

  const caricaDashboard = useCallback(async () => {
    setLoadingDash(true);
    setErrorDash(null);
    try {
      const resp = await fetch('/api/v1/magazzino/dashboard', { headers: authHeaders() });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      setDashboard((await resp.json()) as DashboardData);
    } catch (err) {
      setErrorDash(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingDash(false);
    }
  }, []);

  const caricaTabelle = useCallback(async () => {
    setLoadingTables(true);
    try {
      const [s, e, m] = await Promise.all([
        fetch('/api/v1/magazzino/sotto-soglia', { headers: authHeaders() }),
        fetch('/api/v1/magazzino/in-scadenza', { headers: authHeaders() }),
        fetch('/api/v1/magazzino/movimenti?limit=10', { headers: authHeaders() }),
      ]);
      if (s.ok) setSottoSoglia(await s.json());
      if (e.ok) setInScadenza(await e.json());
      if (m.ok) setMovimenti(await m.json());
    } finally {
      setLoadingTables(false);
    }
  }, []);

  useEffect(() => {
    void caricaDashboard();
    void caricaTabelle();
  }, [caricaDashboard, caricaTabelle]);

  const k = dashboard?.kpi;
  const maxAndamento =
    dashboard?.andamento_6_mesi.reduce(
      (acc, p) => Math.max(acc, p.ingressi + p.uscite),
      0,
    ) ?? 0;
  const maxDistrib =
    dashboard?.distribuzione_categorie.reduce(
      (acc, c) => Math.max(acc, c.count_articoli),
      0,
    ) ?? 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-semibold">Magazzino</h1>
          <p className="text-sm text-muted-foreground">
            Dashboard del ramo operativo corrente
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/magazzino/articoli')}>
            Articoli
          </Button>
          <Button variant="outline" onClick={() => navigate('/magazzino/categorie')}>
            Categorie
          </Button>
          <Button variant="outline" onClick={() => navigate('/magazzino/lotti')}>
            Lotti
          </Button>
          <Button onClick={() => navigate('/magazzino/carico')}>Carico merce</Button>
        </div>
      </div>

      {errorDash && <p className="text-sm text-destructive">Errore dashboard: {errorDash}</p>}
      {loadingDash && <p className="text-sm text-muted-foreground">Caricamento dashboard...</p>}

      {/* Link diretto ai lotti storici senza negozio (nessun endpoint di conteggio
          nuovo: la pagina Lotti gia' filtrata e' sufficiente, cantiere lotti->UO T5). */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm dark:bg-amber-900/20">
        <span className="text-amber-800 dark:text-amber-300">
          Lo storico lotti non ancora attribuito a un negozio va assegnato dalla pagina Lotti.
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/magazzino/lotti?uo=__senza__')}
        >
          Lotti senza negozio
        </Button>
      </div>

      {k && (
        <>
          {/* KPI riga 1 */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Articoli"
              value={fmtInt(k.articoli_totali)}
              sub={`${fmtInt(k.articoli_con_giacenza)} con giacenza`}
              icon={<Package className="h-5 w-5" />}
              onClick={() => navigate('/magazzino/articoli')}
            />
            <KpiCard
              label="Giacenza totale"
              value={fmtDec(k.giacenza_totale)}
              sub={`${fmtInt(k.lotti_totali)} lotti attivi`}
              icon={<Warehouse className="h-5 w-5" />}
              onClick={() => navigate('/magazzino/lotti')}
            />
            <KpiCard
              label="Valore magazzino"
              value={fmtEur(k.valore_magazzino)}
              sub="al costo d'acquisto"
              icon={<TrendingUp className="h-5 w-5" />}
              accent="success"
            />
            <KpiCard
              label="Categorie merceologiche"
              value={fmtInt(k.categorie_root)}
              sub="al livello radice"
              icon={<ArrowLeftRight className="h-5 w-5" />}
              onClick={() => navigate('/magazzino/categorie')}
            />
          </div>

          {/* KPI riga 2 (alert) */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard
              label="Esauriti"
              value={fmtInt(k.articoli_esauriti)}
              sub={`su ${fmtInt(k.articoli_totali)} totali`}
              icon={<Package className="h-5 w-5" />}
              accent={k.articoli_esauriti > 0 ? 'warning' : 'default'}
            />
            <KpiCard
              label="Sotto soglia di riordino"
              value={fmtInt(k.articoli_sotto_soglia)}
              sub="da riordinare"
              icon={<AlertTriangle className="h-5 w-5" />}
              accent={k.articoli_sotto_soglia > 0 ? 'danger' : 'default'}
            />
            <KpiCard
              label="Lotti in scadenza (30gg)"
              value={fmtInt(k.lotti_scadenza_30gg)}
              sub="controllare rotazione"
              icon={<Clock className="h-5 w-5" />}
              accent={k.lotti_scadenza_30gg > 0 ? 'warning' : 'default'}
            />
          </div>

          {/* Andamento 6 mesi */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Andamento movimenti (ultimi 6 mesi)</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard!.andamento_6_mesi.every((p) => p.ingressi === 0 && p.uscite === 0) ? (
                <p className="text-sm text-muted-foreground">
                  Nessun movimento registrato nel periodo.
                </p>
              ) : (
                <div className="space-y-3">
                  {dashboard!.andamento_6_mesi.map((p) => {
                    const maxV = maxAndamento || 1;
                    const pctIn = (p.ingressi / maxV) * 100;
                    const pctOut = (p.uscite / maxV) * 100;
                    return (
                      <div key={p.periodo} className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{monthLabel(p.periodo)}</span>
                          <span>
                            <span className="text-emerald-600">+{fmtInt(p.ingressi)}</span>
                            <span className="mx-2">/</span>
                            <span className="text-destructive">-{fmtInt(p.uscite)}</span>
                          </span>
                        </div>
                        <div className="flex h-3 gap-1">
                          <div
                            className="rounded-sm bg-emerald-500/70"
                            style={{ width: `${pctIn}%` }}
                          />
                          <div
                            className="rounded-sm bg-destructive/70"
                            style={{ width: `${pctOut}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top articoli */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ArrowDown className="h-4 w-4 text-destructive" />
                  Più venduti / usciti (30gg)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {dashboard!.top_uscite_30gg.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">Nessun dato nel periodo.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead className="text-right">Qtà</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboard!.top_uscite_30gg.map((a) => (
                        <TableRow
                          key={a.articolo_id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/magazzino/articoli/${a.articolo_id}`)}
                        >
                          <TableCell className="font-mono text-xs">{a.sku_interno}</TableCell>
                          <TableCell>{a.descrizione ?? '—'}</TableCell>
                          <TableCell className="text-right font-mono">{fmtDec(a.quantita)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ArrowUp className="h-4 w-4 text-emerald-600" />
                  Più caricati / entrati (30gg)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {dashboard!.top_ingressi_30gg.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">Nessun dato nel periodo.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead className="text-right">Qtà</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboard!.top_ingressi_30gg.map((a) => (
                        <TableRow
                          key={a.articolo_id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/magazzino/articoli/${a.articolo_id}`)}
                        >
                          <TableCell className="font-mono text-xs">{a.sku_interno}</TableCell>
                          <TableCell>{a.descrizione ?? '—'}</TableCell>
                          <TableCell className="text-right font-mono">{fmtDec(a.quantita)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Distribuzione per categoria */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Distribuzione articoli per categoria</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard!.distribuzione_categorie.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nessun articolo classificato.
                </p>
              ) : (
                <div className="space-y-2">
                  {dashboard!.distribuzione_categorie.map((c) => {
                    const pct = maxDistrib ? (c.count_articoli / maxDistrib) * 100 : 0;
                    return (
                      <button
                        key={c.categoria_id ?? c.descrizione}
                        type="button"
                        className="block w-full rounded px-2 py-1 text-left transition hover:bg-muted/50"
                        onClick={() => navigate('/magazzino/articoli')}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span>{c.descrizione}</span>
                          <span className="font-mono text-xs text-muted-foreground">
                            {fmtInt(c.count_articoli)}
                          </span>
                        </div>
                        <div className="mt-1 h-2 w-full rounded-sm bg-muted">
                          <div
                            className="h-full rounded-sm bg-primary"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Alert tabelle (preservate) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Articoli sotto soglia di riordino
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingTables && (
              <p className="p-4 text-sm text-muted-foreground">Caricamento...</p>
            )}
            {!loadingTables && sottoSoglia.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">Nessuno.</p>
            )}
            {!loadingTables && sottoSoglia.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="text-right">Giacenza</TableHead>
                    <TableHead className="text-right">Soglia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sottoSoglia.map((item) => (
                    <TableRow
                      key={item.articolo_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/magazzino/articoli/${item.articolo_id}`)}
                    >
                      <TableCell className="font-mono text-xs">{item.sku_interno}</TableCell>
                      <TableCell>{item.descrizione ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="destructive">{fmtDec(item.giacenza)}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {item.soglia_riordino}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4 text-amber-500" />
              Lotti in scadenza
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingTables && (
              <p className="p-4 text-sm text-muted-foreground">Caricamento...</p>
            )}
            {!loadingTables && inScadenza.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">Nessuno.</p>
            )}
            {!loadingTables && inScadenza.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Lotto</TableHead>
                    <TableHead className="text-right">Disp.</TableHead>
                    <TableHead className="text-right">Giorni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inScadenza.map((item) => (
                    <TableRow key={item.lotto_id}>
                      <TableCell className="font-mono text-xs">{item.sku_interno}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {item.numero_lotto ?? '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {fmtDec(item.quantita_disponibile)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={item.giorni_rimanenti <= 0 ? 'destructive' : 'secondary'}>
                          {item.giorni_rimanenti <= 0 ? 'Scaduto' : `${item.giorni_rimanenti}gg`}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ultimi movimenti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ArrowLeftRight className="h-4 w-4" />
            Ultimi movimenti
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingTables && <p className="p-4 text-sm text-muted-foreground">Caricamento...</p>}
          {!loadingTables && movimenti.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">Nessun movimento registrato.</p>
          )}
          {!loadingTables && movimenti.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Quantità</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Riferimento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movimenti.map((mov) => (
                  <TableRow key={mov.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs uppercase">
                        {mov.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span
                        className={
                          mov.quantita > 0 ? 'text-emerald-600' : 'text-destructive'
                        }
                      >
                        {mov.quantita > 0 ? `+${fmtDec(mov.quantita)}` : fmtDec(mov.quantita)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{fmt.dateTime(mov.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      {mov.riferimento_esterno ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagazzinoOverview;
